import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Monitor, 
  Palette, 
  ShoppingCart, 
  Gauge,
  Layout,
  ChevronRight,
  CheckCircle2,
  Star,
  Server
} from 'lucide-react';

// Calendly popup function
const openCalendly = (e) => {
  e.preventDefault();
  if (window.Calendly) {
    window.Calendly.initPopupWidget({
      url: 'https://calendly.com/studioeleven380/30min'
    });
  }
};

const HomePage = () => {
  const heroRef = useRef(null);

  // Mouse parallax effect for 3D elements
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!heroRef.current) return;
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      
      const xPos = (clientX / innerWidth - 0.5) * 2;
      const yPos = (clientY / innerHeight - 0.5) * 2;
      
      const elements = heroRef.current.querySelectorAll('.parallax-element');
      elements.forEach((el, i) => {
        const speed = (i + 1) * 10;
        el.style.transform = `translate(${xPos * speed}px, ${yPos * speed}px)`;
      });

      const rotateElements = heroRef.current.querySelectorAll('.rotate-3d');
      rotateElements.forEach((el) => {
        el.style.transform = `rotateY(${xPos * 10}deg) rotateX(${-yPos * 10}deg)`;
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const services = [
    {
      icon: Monitor,
      title: 'Custom Website Development',
      description: 'Bespoke websites built from scratch. No templates. Designed to convert visitors into customers.',
      link: '/services#custom-websites',
    },
    {
      icon: Layout,
      title: 'Landing Page Development',
      description: 'High-converting landing pages engineered for performance and user engagement.',
      link: '/services#landing-pages',
    },
    {
      icon: ShoppingCart,
      title: 'E-Commerce Development',
      description: 'Online stores that deliver seamless shopping experiences and drive conversions.',
      link: '/services#ecommerce',
    },
    {
      icon: Palette,
      title: 'UI/UX Design',
      description: 'User-centered design that balances aesthetics with functionality and usability.',
      link: '/services#ui-ux',
    },
    {
      icon: Gauge,
      title: 'Website Optimization',
      description: 'Speed optimization and performance tuning for faster, more efficient websites.',
      link: '/services#optimization',
    },
    {
      icon: Server,
      title: 'Deployment & Maintenance',
      description: 'Reliable deployment and ongoing support to keep your website running smoothly.',
      link: '/services#deployment',
    },
  ];

  const stats = [
    { number: '150+', label: 'Websites Delivered' },
    { number: '98%', label: 'Client Satisfaction' },
    { number: '<2s', label: 'Avg Load Time' },
    { number: '24/7', label: 'Support Available' },
  ];

  const testimonials = [
    {
      quote: "Eleven11 completely transformed our website. The new design increased our conversion rate by 85% in the first month.",
      author: "Sarah Mitchell",
      role: "CEO, TechFlow Solutions",
    },
    {
      quote: "The attention to detail and craftsmanship is exceptional. Our website now truly represents our brand's premium positioning.",
      author: "Michael Chen",
      role: "Owner, Premier Auto Group",
    },
    {
      quote: "Working with Eleven11 was seamless. They understood our vision and delivered a website that exceeded our expectations.",
      author: "Amanda Rodriguez",
      role: "Founder, Luxe Interiors",
    },
  ];

  const reasons = [
    'Custom-built websites, no templates',
    'Mobile-first responsive design',
    'Performance-optimized for speed',
    'Conversion-focused user experience',
    'Premium quality craftsmanship',
    'Dedicated project management',
  ];

  return (
    <div data-testid="home-page" className="page-transition">
      {/* Hero Section - 3D Premium */}
      <section 
        ref={heroRef}
        data-testid="hero-section" 
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{ perspective: '1000px' }}
      >
        {/* Animated Background */}
        <div className="absolute inset-0 bg-[#050505]">
          {/* Animated Gradient Mesh */}
          <div className="absolute inset-0">
            <div 
              className="absolute w-[800px] h-[800px] rounded-full opacity-30 parallax-element"
              style={{
                background: 'radial-gradient(circle, rgba(212,175,55,0.15) 0%, transparent 70%)',
                top: '-20%',
                right: '-10%',
                animation: 'pulseGlow 8s ease-in-out infinite',
              }}
            />
            <div 
              className="absolute w-[600px] h-[600px] rounded-full opacity-20 parallax-element"
              style={{
                background: 'radial-gradient(circle, rgba(212,175,55,0.1) 0%, transparent 70%)',
                bottom: '-10%',
                left: '-5%',
                animation: 'pulseGlow 10s ease-in-out infinite',
                animationDelay: '2s',
              }}
            />
          </div>

          {/* 3D Rotating Wireframe Cube */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] opacity-[0.08]" style={{ perspective: '800px' }}>
            <div 
              className="w-full h-full relative"
              style={{
                transformStyle: 'preserve-3d',
                animation: 'rotateCube 30s linear infinite',
              }}
            >
              {/* Cube faces */}
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-full h-full border border-[#D4AF37]"
                  style={{
                    transform: [
                      'translateZ(300px)',
                      'translateZ(-300px)',
                      'rotateY(90deg) translateZ(300px)',
                      'rotateY(-90deg) translateZ(300px)',
                      'rotateX(90deg) translateZ(300px)',
                      'rotateX(-90deg) translateZ(300px)',
                    ][i],
                  }}
                />
              ))}
            </div>
          </div>

          {/* Floating 3D Rings */}
          <div 
            className="absolute top-20 right-[15%] w-32 h-32 border-2 border-[#D4AF37]/20 rounded-full parallax-element"
            style={{
              animation: 'floatRing 6s ease-in-out infinite',
              transformStyle: 'preserve-3d',
            }}
          />
          <div 
            className="absolute bottom-32 left-[10%] w-24 h-24 border border-[#D4AF37]/15 rounded-full parallax-element"
            style={{
              animation: 'floatRing 8s ease-in-out infinite reverse',
              animationDelay: '1s',
            }}
          />
          <div 
            className="absolute top-1/3 left-[20%] w-16 h-16 border border-[#D4AF37]/10 rounded-full parallax-element"
            style={{
              animation: 'floatRing 5s ease-in-out infinite',
              animationDelay: '2s',
            }}
          />

          {/* Moving Light Beams */}
          <div className="absolute inset-0 overflow-hidden">
            <div 
              className="absolute w-[2px] h-[150%] bg-gradient-to-b from-transparent via-[#D4AF37]/30 to-transparent"
              style={{
                left: '20%',
                top: '-25%',
                transform: 'rotate(25deg)',
                animation: 'beamMove 4s ease-in-out infinite',
              }}
            />
            <div 
              className="absolute w-[1px] h-[150%] bg-gradient-to-b from-transparent via-[#D4AF37]/20 to-transparent"
              style={{
                right: '30%',
                top: '-25%',
                transform: 'rotate(-20deg)',
                animation: 'beamMove 6s ease-in-out infinite',
                animationDelay: '2s',
              }}
            />
          </div>

          {/* Dot Grid Pattern */}
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: 'radial-gradient(#D4AF37 1px, transparent 1px)',
              backgroundSize: '50px 50px',
            }}
          />
        </div>

        {/* Hero Content */}
        <div className="container-custom relative z-10 pt-28 pb-16">
          <div className="max-w-5xl mx-auto text-center rotate-3d" style={{ transformStyle: 'preserve-3d' }}>
            {/* Overline with animated line */}
            <div className="flex items-center justify-center gap-4 mb-6 animate-fade-in">
              <div className="h-[1px] w-16 bg-gradient-to-r from-transparent to-[#D4AF37]" />
              <span className="overline tracking-[0.3em]">Eleven11 Studios</span>
              <div className="h-[1px] w-16 bg-gradient-to-l from-transparent to-[#D4AF37]" />
            </div>
            
            {/* Main Headline - Massive */}
            <h1 
              data-testid="hero-headline"
              className="text-5xl sm:text-6xl md:text-7xl lg:text-[7rem] font-bold text-[#F5F5F7] mb-4 tracking-tighter leading-[0.9] animate-fade-in-up"
              style={{ animationDelay: '0.1s' }}
            >
              <span className="text-gradient-gold">Websites</span>
              <br />
              <span className="text-[#F5F5F7]/90">That</span>
              <span className="text-gradient-gold"> Convert</span>
            </h1>
            
            {/* Minimal Subtext */}
            <p 
              className="text-base md:text-lg text-[#71717A] max-w-2xl mx-auto mb-8 animate-fade-in-up font-light"
              style={{ animationDelay: '0.2s' }}
            >
              Premium web design & development studio crafting high-performance websites that deliver exceptional user experiences and drive conversions.
            </p>
            
            {/* CTA Buttons */}
            <div 
              className="flex flex-wrap justify-center gap-4 animate-fade-in-up"
              style={{ animationDelay: '0.3s' }}
            >
              <button
                onClick={openCalendly}
                data-testid="hero-cta-primary"
                className="btn-primary group relative overflow-hidden"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Start Your Project
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-[#C5A059] to-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
              <Link
                to="/services"
                data-testid="hero-cta-secondary"
                className="btn-secondary group"
              >
                <span>View Our Work</span>
              </Link>
            </div>

            {/* Floating Stats Preview */}
            <div 
              className="mt-16 flex flex-wrap justify-center gap-12 md:gap-20 animate-fade-in-up"
              style={{ animationDelay: '0.5s' }}
            >
              {[
                { value: '150+', label: 'Websites' },
                { value: '<2s', label: 'Load Time' },
                { value: '98%', label: 'Satisfaction' },
              ].map((stat, i) => (
                <div key={i} className="text-center group">
                  <div className="text-3xl md:text-4xl font-bold text-[#D4AF37] group-hover:scale-110 transition-transform">{stat.value}</div>
                  <div className="text-[10px] text-[#71717A] uppercase tracking-[0.2em] mt-2">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Video Background */}
        <video 
          autoPlay 
          loop 
          muted 
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-20 mix-blend-lighten"
          style={{ filter: 'hue-rotate(30deg) saturate(0.5)' }}
        >
          <source src="https://assets.mixkit.co/videos/preview/mixkit-digital-animation-of-futuristic-devices-99786-large.mp4" type="video/mp4" />
        </video>

        {/* Scroll indicator - positioned lower */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20">
          <div 
            className="w-6 h-10 border border-[#D4AF37]/30 rounded-full flex justify-center pt-2 cursor-pointer hover:border-[#D4AF37]/60 transition-colors"
            onClick={() => window.scrollBy({ top: window.innerHeight, behavior: 'smooth' })}
          >
            <div 
              className="w-1 h-2 bg-[#D4AF37] rounded-full"
              style={{ animation: 'scrollIndicator 1.5s ease-in-out infinite' }}
            />
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section data-testid="trust-bar-section" className="py-16 border-y border-white/5 bg-[#050505]">
        <div className="container-custom">
          <p className="text-center text-[#71717A] text-sm mb-10 tracking-wide uppercase">
            Trusted by Businesses Across Industries
          </p>
          <div className="flex flex-wrap items-center justify-center gap-12 md:gap-20">
            <div className="trust-logo">
              <span className="text-2xl font-bold text-white/50 tracking-tight">Shopify</span>
            </div>
            <div className="trust-logo">
              <span className="text-2xl font-bold text-white/50 tracking-tight">WordPress</span>
            </div>
            <div className="trust-logo">
              <span className="text-xl text-white/30 italic">+ Your Brand</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges / Certifications */}
      <section data-testid="trust-badges-section" className="py-12 bg-[#0a0a0a]">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            {/* Web Standards */}
            <div className="flex items-center gap-3 px-6 py-3 border border-white/10 bg-[#111111]">
              <Monitor className="w-6 h-6 text-[#D4AF37]" />
              <span className="text-white/70 text-sm font-medium">W3C Compliant</span>
            </div>
            
            {/* Mobile First */}
            <div className="flex items-center gap-3 px-6 py-3 border border-white/10 bg-[#111111]">
              <Layout className="w-6 h-6 text-[#D4AF37]" />
              <span className="text-white/70 text-sm font-medium">Mobile First</span>
            </div>
            
            {/* Performance */}
            <div className="flex items-center gap-3 px-6 py-3 border border-white/10 bg-[#111111]">
              <Gauge className="w-6 h-6 text-[#D4AF37]" />
              <span className="text-white/70 text-sm font-medium">Performance Optimized</span>
            </div>
            
            {/* 5 Star Rated */}
            <div className="flex items-center gap-3 px-6 py-3 border border-white/10 bg-[#111111]">
              <div className="flex">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" />
                ))}
              </div>
              <span className="text-white/70 text-sm font-medium">5-Star Rated</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section data-testid="services-section" className="section-padding bg-[#050505]">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="overline">What We Build</span>
            <h2 className="heading-section text-[#F5F5F7] mt-4 mb-6">
              Web Design & Development Services
            </h2>
            <p className="body-large">
              We craft premium websites that combine beautiful design with 
              exceptional performance and user experience.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 stagger-children">
            {services.map((service, index) => (
              <Link
                key={service.title}
                to={service.link}
                data-testid={`service-card-${service.title.toLowerCase().replace(/ /g, '-')}`}
                className="service-card-numbered group"
                data-number={`0${index + 1}`}
              >
                <service.icon className="w-10 h-10 text-[#D4AF37] mb-6" />
                <h3 className="heading-card text-[#F5F5F7] mb-4 group-hover:text-[#D4AF37] transition-colors">
                  {service.title}
                </h3>
                <p className="body-regular mb-6">{service.description}</p>
                <span className="inline-flex items-center gap-2 text-[#D4AF37] text-sm font-medium">
                  Learn More
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section data-testid="stats-section" className="py-20 bg-[#111111]">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="stat-item">
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section data-testid="why-choose-section" className="section-padding bg-[#050505]">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="overline">Why Eleven11</span>
              <h2 className="heading-section text-[#F5F5F7] mt-4 mb-6">
                Craftsmanship Meets Performance
              </h2>
              <p className="body-large mb-10">
                We don't do cookie-cutter solutions. Every website is built from scratch, 
                designed specifically for your brand and optimized for your users.
              </p>
              <ul className="space-y-4">
                {reasons.map((reason) => (
                  <li key={reason} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span className="text-[#A1A1AA]">{reason}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="aspect-square bg-[#111111] border border-white/10 p-8 flex items-center justify-center overflow-hidden">
                {/* Animated Background Grid */}
                <div className="absolute inset-0 opacity-10">
                  <div 
                    className="absolute inset-0"
                    style={{
                      backgroundImage: 'linear-gradient(#D4AF37 1px, transparent 1px), linear-gradient(90deg, #D4AF37 1px, transparent 1px)',
                      backgroundSize: '40px 40px',
                      animation: 'gridMove 20s linear infinite',
                    }}
                  />
                </div>
                
                {/* Orbiting Rings */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div 
                    className="absolute w-64 h-64 border border-[#D4AF37]/20 rounded-full"
                    style={{ animation: 'spin 15s linear infinite' }}
                  />
                  <div 
                    className="absolute w-48 h-48 border border-[#D4AF37]/30 rounded-full"
                    style={{ animation: 'spin 12s linear infinite reverse' }}
                  />
                  <div 
                    className="absolute w-32 h-32 border border-[#D4AF37]/40 rounded-full"
                    style={{ animation: 'spin 8s linear infinite' }}
                  />
                </div>
                
                {/* Floating Particles */}
                {[...Array(12)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-1 h-1 bg-[#D4AF37] rounded-full"
                    style={{
                      left: `${20 + Math.random() * 60}%`,
                      top: `${20 + Math.random() * 60}%`,
                      animation: `floatParticle ${3 + Math.random() * 4}s ease-in-out infinite`,
                      animationDelay: `${i * 0.3}s`,
                      opacity: 0.4 + Math.random() * 0.4,
                    }}
                  />
                ))}
                
                {/* Main 11:11 Text */}
                <div className="text-center relative z-10">
                  <div 
                    className="text-7xl md:text-8xl font-bold mb-4 relative"
                    style={{ 
                      background: 'linear-gradient(135deg, #D4AF37 0%, #F5E6B8 50%, #D4AF37 100%)',
                      backgroundSize: '200% 200%',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      animation: 'shimmer 3s ease-in-out infinite',
                    }}
                  >
                    11:11
                  </div>
                  <div className="relative">
                    <p 
                      className="text-[#A1A1AA] text-sm uppercase tracking-[0.25em]"
                      style={{ animation: 'fadeInUp 1s ease-out' }}
                    >
                      Where Design Meets Function
                    </p>
                    <div 
                      className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent"
                      style={{ animation: 'expandLine 2s ease-in-out infinite' }}
                    />
                  </div>
                </div>
                
                {/* Corner Accents */}
                <div className="absolute top-4 left-4 w-8 h-8 border-t border-l border-[#D4AF37]/50" style={{ animation: 'pulse 2s ease-in-out infinite' }} />
                <div className="absolute top-4 right-4 w-8 h-8 border-t border-r border-[#D4AF37]/50" style={{ animation: 'pulse 2s ease-in-out infinite', animationDelay: '0.5s' }} />
                <div className="absolute bottom-4 left-4 w-8 h-8 border-b border-l border-[#D4AF37]/50" style={{ animation: 'pulse 2s ease-in-out infinite', animationDelay: '1s' }} />
                <div className="absolute bottom-4 right-4 w-8 h-8 border-b border-r border-[#D4AF37]/50" style={{ animation: 'pulse 2s ease-in-out infinite', animationDelay: '1.5s' }} />
              </div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 border border-[#D4AF37]/30" />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials-section" className="section-padding bg-[#0a0a0a]">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="overline">Client Stories</span>
            <h2 className="heading-section text-[#F5F5F7] mt-4">
              What Our Clients Say
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                data-testid={`testimonial-card-${index}`}
                className="testimonial-card"
              >
                <p className="text-[#A1A1AA] leading-relaxed mb-8 relative z-10">
                  "{testimonial.quote}"
                </p>
                <div className="border-t border-white/10 pt-6">
                  <p className="text-[#F5F5F7] font-medium">{testimonial.author}</p>
                  <p className="text-[#71717A] text-sm">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="section-padding bg-[#050505] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/5 to-transparent" />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="overline">Let's Talk</span>
            <h2 className="heading-section text-[#F5F5F7] mt-4 mb-6">
              Ready to Build Your Website?
            </h2>
            <p className="body-large mb-10">
              Book a free consultation and discover how we can help you create 
              a high-performance website that converts visitors into customers.
            </p>
            <button
              onClick={openCalendly}
              data-testid="final-cta-button"
              className="btn-primary inline-flex"
            >
              Book Your Free Consultation
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
